import ReactApexChart from 'react-apexcharts';
import { Box, Typography, Grid } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { ChartType, ChartOptions } from '../../types/dashboard';

export interface ChartTemplate {
  id: string;
  name: string;
  desc: string;
  type: ChartType;
  chartOptions: ChartOptions;
}

const PALETTE_COLORS: Record<string, string[]> = {
  palette1:  ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'],
  palette2:  ['#3F51B5', '#03A9F4', '#4CAF50', '#F9CE1D', '#FF9800'],
  palette3:  ['#33B2DF', '#546E7A', '#D4526E', '#13D8AA', '#A5978B'],
  palette4:  ['#4ECDC4', '#C7F464', '#FF6B6B', '#88D8B0', '#A8E6CF'],
  palette5:  ['#FD6A6A', '#4CA3DD', '#A1DE93', '#FDDD8C', '#EB84B5'],
  palette6:  ['#2E294E', '#1B998B', '#E84855', '#FF9F1C', '#FFBF69'],
  palette7:  ['#00CC99', '#00CCFF', '#0066CC', '#3300FF', '#CC33FF'],
  palette8:  ['#D7263D', '#1B1F3B', '#02A9EA', '#85BB65', '#F46036'],
  palette9:  ['#F7B731', '#FC5C65', '#26de81', '#45AAF2', '#A55EEA'],
  palette10: ['#0652DD', '#1289A7', '#C4E538', '#FDA7DF', '#ED4C67'],
};

export const CHART_TEMPLATES: ChartTemplate[] = [
  {
    id: 'realtime-line',
    name: 'Çizgi Grafik',
    desc: 'Zaman eksenli sensör verisi',
    type: 'line',
    chartOptions: { palette: 'palette2', strokeCurve: 'smooth', strokeWidth: 3, animations: true, toolbar: true, dataLabels: false, fillType: 'solid' },
  },
  {
    id: 'gradient-area',
    name: 'Gradient Dalga',
    desc: 'Trend & değişim analizi',
    type: 'area',
    chartOptions: { palette: 'palette1', strokeCurve: 'smooth', strokeWidth: 2, fillType: 'gradient', animations: true, toolbar: true, dataLabels: false },
  },
  {
    id: 'column-bar',
    name: 'Sütun Grafik',
    desc: 'Periyodik karşılaştırma',
    type: 'bar',
    chartOptions: { palette: 'palette4', horizontal: false, stacked: false, animations: true, toolbar: true, dataLabels: true },
  },
  {
    id: 'horizontal-bar',
    name: 'Yatay Bar',
    desc: 'Key başına anlık son değer',
    type: 'bar',
    chartOptions: { palette: 'palette7', horizontal: true, timeGrouped: false, stacked: false, animations: true, toolbar: true, dataLabels: true },
  },
  {
    id: 'grouped-bar',
    name: 'Grouped Bar',
    desc: 'Zaman diliminde key\'ler yan yana',
    type: 'bar',
    chartOptions: { palette: 'palette8', horizontal: true, timeGrouped: true, stacked: false, animations: true, toolbar: true, dataLabels: false },
  },
  {
    id: 'radial-gauge',
    name: 'Radyal Gösterge',
    desc: 'Anlık tek değer göstergesi',
    type: 'radialBar',
    chartOptions: { palette: 'palette3', animations: true, toolbar: false, dataLabels: true },
  },
  {
    id: 'semi-gauge',
    name: 'Yarım Gösterge',
    desc: 'Semicircle anlık değer göstergesi',
    type: 'radialBar',
    chartOptions: { palette: 'palette2', animations: true, toolbar: false, dataLabels: true, semicircle: true },
  },
  {
    id: 'heatmap',
    name: 'Isı Haritası',
    desc: 'Zaman-yoğunluk analizi',
    type: 'heatmap',
    chartOptions: { palette: 'palette9', animations: false, toolbar: false, dataLabels: false },
  },
  {
    id: 'pie',
    name: 'Pasta Grafik',
    desc: 'Oran & dağılım analizi',
    type: 'pie',
    chartOptions: { palette: 'palette1', animations: true, toolbar: false, dataLabels: true },
  },
  {
    id: 'donut',
    name: 'Halka Grafik',
    desc: 'Merkez değerli dağılım',
    type: 'donut',
    chartOptions: { palette: 'palette6', animations: true, toolbar: false, dataLabels: true },
  },
];

const now = Date.now();

const LINE_SERIES = [
  {
    name: 'Sensör',
    data: [42, 55, 38, 70, 52, 68, 45, 73, 60, 80, 55, 65].map((v, i) => [
      now - (11 - i) * 5 * 60000, v,
    ]) as [number, number][],
  },
];

const AREA_SERIES = [
  {
    name: 'Sıcaklık',
    data: [28, 45, 35, 60, 42, 75, 50, 68, 40, 72, 55, 80].map((v, i) => [
      now - (11 - i) * 5 * 60000, v,
    ]) as [number, number][],
  },
  {
    name: 'Nem',
    data: [15, 25, 20, 35, 28, 42, 30, 45, 25, 50, 38, 55].map((v, i) => [
      now - (11 - i) * 5 * 60000, v,
    ]) as [number, number][],
  },
];

const BAR_SERIES = [{ name: 'Değer', data: [44, 55, 57, 56, 61, 58, 63, 60] }];
const BAR_CATEGORIES = ['01', '02', '03', '04', '05', '06', '07', '08'];

const RADIAL_SERIES = [67, 84, 55];

const HEATMAP_SERIES = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'].map(day => ({
  name: day,
  data: Array.from({ length: 8 }, (_, h) => ({
    x: `${String(h * 3).padStart(2, '0')}:00`,
    y: Math.floor(20 + Math.random() * 80),
  })),
}));


const PIE_SERIES = [44, 55, 41, 17, 15];
const PIE_LABELS = ['Sensör A', 'Sensör B', 'Sensör C', 'Sensör D', 'Sensör E'];

const buildPreviewOptions = (template: ChartTemplate): ApexOptions => {
  const o = template.chartOptions;
  const colors = PALETTE_COLORS[o.palette ?? 'palette1'] ?? PALETTE_COLORS.palette1;
  const base: ApexOptions = {
    chart: {
      id: `preview-${template.id}`,
      type: template.type as any,
      background: 'transparent',
      toolbar: { show: false },
      sparkline: { enabled: false },
      animations: { enabled: false },
    },
    colors,
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: { enabled: false },
    grid: { borderColor: '#e2e8f0', strokeDashArray: 4 },
    noData: { text: '' },
  };

  if (template.type === 'line') {
    return {
      ...base,
      stroke: { curve: 'smooth', width: 3 },
      xaxis: { type: 'datetime', labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { show: false },
      fill: { type: 'solid' },
    };
  }

  if (template.type === 'area') {
    return {
      ...base,
      stroke: { curve: 'smooth', width: 2 },
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.6, opacityTo: 0.05, stops: [0, 100] } },
      xaxis: { type: 'datetime', labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { show: false },
    };
  }

  if (template.type === 'bar') {
    const horizontal = o.horizontal ?? false;
    return {
      ...base,
      plotOptions: { bar: { horizontal, borderRadius: 4, columnWidth: '60%', barHeight: '60%' } },
      xaxis: {
        categories: BAR_CATEGORIES,
        labels: horizontal
          ? { show: false }
          : { style: { colors: '#94a3b8', fontSize: '10px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { colors: '#94a3b8', fontSize: '10px' } } },
    };
  }

  if (template.type === 'radialBar') {
    if (template.chartOptions.semicircle) {
      return {
        ...base,
        chart: { ...base.chart, offsetY: -10, sparkline: { enabled: true } },
        plotOptions: {
          radialBar: {
            startAngle: -90,
            endAngle: 90,
            track: { background: '#e7e7e7', strokeWidth: '97%', margin: 5, dropShadow: { enabled: true, top: 2, left: 0, color: '#444', opacity: 0.5, blur: 2 } },
            dataLabels: { name: { show: false }, value: { offsetY: -2, fontSize: '18px', color: '#1e293b', fontWeight: 700 } },
          },
        },
        grid: { padding: { top: -10 } },
        fill: { type: 'gradient', gradient: { shade: 'light', shadeIntensity: 0.4, inverseColors: false, opacityFrom: 1, opacityTo: 1, stops: [0, 50, 53, 91] } },
        labels: ['Değer'],
      };
    }
    return {
      ...base,
      plotOptions: {
        radialBar: {
          hollow: { size: '40%' },
          track: { background: '#334155' },
          dataLabels: {
            name: { show: true, fontSize: '10px', color: '#94a3b8' },
            value: { show: true, fontSize: '14px', color: '#f1f5f9', fontWeight: 700 },
          },
        },
      },
      labels: ['Sıc', 'Nem', 'Bas'],
    };
  }

  if (template.type === 'heatmap') {
    return {
      ...base,
      plotOptions: { heatmap: { colorScale: { inverse: false } } },
      xaxis: { labels: { show: false }, axisBorder: { show: false } },
      yaxis: { labels: { style: { colors: '#94a3b8', fontSize: '9px' } } },
    };
  }

  if (template.type === 'pie' || template.type === 'donut') {
    return {
      ...base,
      labels: PIE_LABELS,
      plotOptions: { pie: { donut: { size: template.type === 'donut' ? '55%' : '0%' } } },
      legend: { show: false },
    };
  }

  return base;
};

const getSampleSeries = (template: ChartTemplate) => {
  if (template.type === 'line') return LINE_SERIES;
  if (template.type === 'area') return AREA_SERIES;
  if (template.type === 'bar') return BAR_SERIES;
  if (template.type === 'radialBar') return RADIAL_SERIES;
  if (template.type === 'heatmap') return HEATMAP_SERIES;
  if (template.type === 'pie') return PIE_SERIES;
  if (template.type === 'donut') return PIE_SERIES;
  return LINE_SERIES;
};

interface Props {
  selected: string;
  onSelect: (template: ChartTemplate) => void;
}

const ChartTemplatePicker = ({ selected, onSelect }: Props) => (
  <Grid container spacing={2}>
    {CHART_TEMPLATES.map(template => {
      const isSelected = selected === template.id;
      return (
        <Grid size={{ xs: 12, sm: 6 }} key={template.id}>
          <Box
            onClick={() => onSelect(template)}
            sx={{
              border: 2,
              borderColor: isSelected ? 'primary.main' : 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              cursor: 'pointer',
              backgroundColor: 'background.paper',
              transition: 'all 0.2s',
              display: 'flex',
              flexDirection: 'column',
              height: 220,
              '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
            }}
          >
            {/* Mini chart preview */}
            <Box sx={{ backgroundColor: 'grey.50', px: 0.5, pt: 0.5, flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100%' }}>
                <ReactApexChart
                  key={template.id}
                  type={template.type as any}
                  series={getSampleSeries(template) as any}
                  options={buildPreviewOptions(template)}
                  height={140}
                />
              </Box>
            </Box>

            {/* Label */}
            <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {template.name}
                </Typography>
                {isSelected && (
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'primary.main' }} />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">{template.desc}</Typography>
            </Box>
          </Box>
        </Grid>
      );
    })}
  </Grid>
);

export default ChartTemplatePicker;
